import React, { useState, useEffect, useRef, useCallback } from "react";
import { Helmet } from "react-helmet";
import { useAuthContext } from "../../context/AuthContext";
import {
  connectiqChat,
  connectiqGetSessions,
  connectiqGetMessages,
  connectiqDeleteSession,
} from "../../apis/connectiqAPI";
import { useHistory } from "react-router-dom";

// ── Helpers ───────────────────────────────────────────────────────────────────

const groupSessionsByDate = (sessions) => {
  const today = new Date().toDateString();
  const yesterday = new Date(Date.now() - 86400000).toDateString();
  const groups = { Today: [], Yesterday: [], Earlier: [] };
  sessions.forEach((s) => {
    const d = new Date(s.updated_at).toDateString();
    if (d === today) groups.Today.push(s);
    else if (d === yesterday) groups.Yesterday.push(s);
    else groups.Earlier.push(s);
  });
  return groups;
};

const formatTime = (dateStr) =>
  new Date(dateStr).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });

const renderTextContent = (text) =>
  (text || "").split("\n").map((line, i) => (
    <p key={i} style={{ margin: "3px 0", lineHeight: "1.6" }}>
      {line || <br />}
    </p>
  ));

const initials = (name) =>
  (name || "?")
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

// ── Structured Result Cards ───────────────────────────────────────────────────

const AVATAR_COLORS = [
  { bg: "#f0eefe", color: "#5b4fd6" },
  { bg: "#eef9ee", color: "#2f7a3c" },
  { bg: "#fef4e8", color: "#b5750f" },
  { bg: "#fdeeee", color: "#b3402e" },
  { bg: "#eef6fd", color: "#1b6fa8" },
];

const BADGE_STYLES = {
  "Best fit": { bg: "#f0eefe", color: "#5b4fd6" },
  "Strong fit": { bg: "#eef9ee", color: "#2f7a3c" },
  "Good fit": { bg: "#fef4e8", color: "#b5750f" },
};

const StructuredResult = ({ data }) => (
  <div style={{ fontSize: "13.5px", maxWidth: "100%" }}>
    {data.location_warning && data.location_warning_message && (
      <div
        style={{
          background: "#fdf6ec",
          borderRadius: "10px",
          padding: "10px 13px",
          color: "#946215",
          fontSize: "12px",
          marginBottom: "12px",
          display: "flex",
          gap: "7px",
          alignItems: "flex-start",
        }}
      >
        <span style={{ fontSize: "15px", flexShrink: 0 }}>⚠</span>
        <span>{data.location_warning_message}</span>
      </div>
    )}

    {data.summary && (
      <p style={{ color: "#555", marginBottom: "14px", lineHeight: "1.6" }}>
        {data.summary}
      </p>
    )}

    {data.coverage && data.coverage.length > 0 && (
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "6px",
          marginBottom: "12px",
        }}
      >
        {data.coverage.map((c, i) => (
          <span
            key={i}
            style={{
              fontSize: "11px",
              padding: "4px 10px",
              borderRadius: "100px",
              fontWeight: "500",
              display: "flex",
              alignItems: "center",
              gap: "4px",
              background: c.met ? "#eef9ee" : "#fdeeee",
              color: c.met ? "#2f7a3c" : "#b3402e",
            }}
          >
            {c.met ? "✓" : "✗"} {c.label}
          </span>
        ))}
      </div>
    )}

    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "6px",
        marginBottom: "12px",
        color: "#6C63FF",
        fontSize: "12px",
        fontWeight: "500",
      }}
    >
      <span>👥</span>
      <span>
        {data.candidates?.length || 0} candidates found
        {data.total_searched
          ? ` · ${data.total_searched} profiles searched`
          : ""}
      </span>
    </div>

    <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
      {(data.candidates || []).map((c, i) => {
        const av = AVATAR_COLORS[i % AVATAR_COLORS.length];
        const badgeStyle = BADGE_STYLES[c.badge] || BADGE_STYLES["Good fit"];
        return (
          <div
            key={i}
            style={{
              background: "#fff",
              border: i === 0 ? "1px solid #171717" : "0.5px solid #ececec",
              borderRadius: "12px",
              padding: "14px 16px",
              display: "flex",
              gap: "12px",
              alignItems: "flex-start",
            }}
          >
            <div
              style={{
                width: "38px",
                height: "38px",
                borderRadius: "10px",
                background: av.bg,
                color: av.color,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "12px",
                fontWeight: "600",
                flexShrink: 0,
              }}
            >
              {initials(c.name)}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  fontWeight: "500",
                  fontSize: "13.5px",
                  color: "#171717",
                  marginBottom: "2px",
                }}
              >
                {c.name}
              </div>
              <div
                style={{
                  fontSize: "12px",
                  color: "#8c8c8c",
                  marginBottom: "8px",
                }}
              >
                {c.role}
                {c.location ? ` · ${c.location}` : ""}
              </div>
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "5px",
                  marginBottom: "8px",
                }}
              >
                {(c.skills || []).slice(0, 3).map((s, si) => (
                  <span
                    key={si}
                    style={{
                      fontSize: "10.5px",
                      padding: "3px 9px",
                      borderRadius: "100px",
                      background: "#f0eefe",
                      color: "#5b4fd6",
                      fontWeight: "500",
                    }}
                  >
                    {s}
                  </span>
                ))}
                {c.experience && (
                  <span
                    style={{
                      fontSize: "10.5px",
                      padding: "3px 9px",
                      borderRadius: "100px",
                      background: "#fef4e8",
                      color: "#b5750f",
                      fontWeight: "500",
                    }}
                  >
                    {c.experience}
                  </span>
                )}
                {c.availability && (
                  <span
                    style={{
                      fontSize: "10.5px",
                      padding: "3px 9px",
                      borderRadius: "100px",
                      background: "#eef9ee",
                      color: "#2f7a3c",
                      fontWeight: "500",
                    }}
                  >
                    {c.availability}
                  </span>
                )}
              </div>
              {c.evidence && (
                <div
                  style={{
                    fontSize: "11.5px",
                    color: "#999",
                    borderLeft: "2px solid #ececec",
                    paddingLeft: "9px",
                    lineHeight: "1.5",
                  }}
                >
                  {c.evidence}
                </div>
              )}
            </div>
            <div style={{ flexShrink: 0, textAlign: "right" }}>
              <div
                style={{
                  fontSize: "18px",
                  fontWeight: "500",
                  color: "#171717",
                }}
              >
                {c.match_score}%
              </div>
              <div
                style={{
                  fontSize: "10px",
                  color: "#b8b8b8",
                  marginBottom: "5px",
                }}
              >
                match
              </div>
              {c.badge && (
                <span
                  style={{
                    display: "inline-block",
                    fontSize: "10px",
                    padding: "3px 9px",
                    borderRadius: "100px",
                    fontWeight: "500",
                    background: badgeStyle.bg,
                    color: badgeStyle.color,
                  }}
                >
                  {c.badge}
                </span>
              )}
            </div>
          </div>
        );
      })}
    </div>

    {data.gap_message && (
      <div
        style={{
          background: "#fdf6ec",
          borderRadius: "10px",
          padding: "10px 13px",
          color: "#946215",
          fontSize: "12px",
          marginTop: "12px",
          display: "flex",
          gap: "7px",
          alignItems: "flex-start",
        }}
      >
        <span style={{ fontSize: "15px", flexShrink: 0 }}>⚠</span>
        <span>{data.gap_message}</span>
      </div>
    )}
  </div>
);

// ── Main Screen ───────────────────────────────────────────────────────────────

const ConnectIQScreen = () => {
  const { userDetails } = useAuthContext();
  const history = useHistory();
  const parsedUser = userDetails ? JSON.parse(userDetails) : null;
  const userSlug = parsedUser?.user_slug || "";
  const userInitials = parsedUser?.user_name
    ? initials(parsedUser.user_name)
    : "ME";
  const userPrimaryRole = parsedUser?.user_primary_role;

  const [scope, setScope] = useState("my_company");
  const [sessions, setSessions] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [message, setMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);

  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);

  const EXAMPLES = [
    "Find a project manager with Oil and Gas experience in Asia",
    "I need a team of 4 for a 6-month healthcare software project in Nova Scotia",
    "Who are our strongest React developers available full time?",
    "Find senior backend developers with 5+ years experience",
  ];

  useEffect(() => {
    if (!userSlug) {
      history.push("/sign-in");
      return;
    }
    if (userPrimaryRole && userPrimaryRole !== "2") {
      history.push("/dashboard");
      return;
    }
    loadSessions();
  }, [userSlug, userPrimaryRole]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isLoading]);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height =
        Math.min(textareaRef.current.scrollHeight, 120) + "px";
    }
  }, [message]);

  const loadSessions = async () => {
    const res = await connectiqGetSessions(userSlug);
    if (res?.data?.status === 1) setSessions(res.data.sessions || []);
  };

  const parseMessages = (rawMessages) =>
    (rawMessages || []).map((msg) => {
      if (msg.role === "assistant") {
        try {
          const json = JSON.parse(msg.content);
          if (json && json.candidates)
            return { role: "assistant", type: "structured", data: json };
        } catch (e) {}
        return { role: "assistant", type: "text", content: msg.content };
      }
      return { role: "user", content: msg.content };
    });

  const loadSession = async (sid) => {
    setIsLoadingHistory(true);
    setSessionId(sid);
    setMessages([]);
    const res = await connectiqGetMessages(userSlug, sid);
    if (res?.data?.status === 1) setMessages(parseMessages(res.data.messages));
    setIsLoadingHistory(false);
  };

  const startNewChat = () => {
    setSessionId(null);
    setMessages([]);
    setMessage("");
    textareaRef.current?.focus();
  };

  const handleSend = useCallback(
    async (overrideMessage) => {
      const text = (overrideMessage || message).trim();
      if (!text || isLoading) return;

      const userMsg = { role: "user", content: text };
      setMessages((prev) => [...prev, userMsg]);
      setMessage("");
      setIsLoading(true);

      const res = await connectiqChat(userSlug, text, scope, sessionId);

      if (res?.data?.status === 1) {
        const aiMsg =
          res.data.type === "structured"
            ? { role: "assistant", type: "structured", data: res.data.data }
            : { role: "assistant", type: "text", content: res.data.message };
        setMessages((prev) => [...prev, aiMsg]);
        if (!sessionId) setSessionId(res.data.session_id);
        loadSessions();
      } else {
        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            type: "text",
            content:
              res?.data?.message || "Something went wrong. Please try again.",
          },
        ]);
      }

      setIsLoading(false);
      textareaRef.current?.focus();
    },
    [message, isLoading, userSlug, scope, sessionId],
  );

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleDeleteSession = async (e, sid) => {
    e.stopPropagation();
    await connectiqDeleteSession(userSlug, sid);
    if (sessionId === sid) startNewChat();
    loadSessions();
  };

  const grouped = groupSessionsByDate(sessions);
  const currentTitle = sessionId
    ? sessions.find((s) => s.session_id === sessionId)?.session_title
    : null;

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <>
      <Helmet>
        <title>FirmConnector :: ConnectIQ</title>
      </Helmet>

      <div
        style={{
          display: "flex",
          height: "100vh",
          width: "100vw",
          fontFamily: "inherit",
          background: "#fff",
          overflow: "hidden",
        }}
      >
        {/* Sidebar */}
        <div
          style={{
            width: "250px",
            background: "#fafafa",
            borderRight: "0.5px solid #ececec",
            display: "flex",
            flexDirection: "column",
            flexShrink: 0,
          }}
        >
          {/* Brand + new chat */}
          <div style={{ padding: "18px 16px 14px" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "9px",
                marginBottom: "16px",
              }}
            >
              <div
                style={{
                  width: "26px",
                  height: "26px",
                  borderRadius: "7px",
                  background: "#171717",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#fff",
                  fontSize: "11px",
                  fontWeight: "600",
                  flexShrink: 0,
                }}
              >
                IQ
              </div>
              <span
                style={{
                  fontSize: "14px",
                  fontWeight: "500",
                  color: "#171717",
                  letterSpacing: "-0.2px",
                }}
              >
                ConnectIQ
              </span>
              <button
                onClick={() => history.push("/dashboard")}
                title="Back to app"
                style={{
                  marginLeft: "auto",
                  width: "26px",
                  height: "26px",
                  borderRadius: "50%",
                  border: "none",
                  background: "transparent",
                  color: "#b0b0b0",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "15px",
                }}
              >
                ←
              </button>
            </div>
            <button
              onClick={startNewChat}
              style={{
                width: "100%",
                padding: "9px 12px",
                borderRadius: "9px",
                border: "none",
                background: "#171717",
                color: "#fff",
                fontSize: "12.5px",
                fontWeight: "500",
                cursor: "pointer",
                textAlign: "left",
                display: "flex",
                alignItems: "center",
                gap: "7px",
              }}
            >
              + New chat
            </button>
          </div>

          {/* History */}
          <div style={{ flex: 1, overflowY: "auto", padding: "16px 0 0" }}>
            {["Today", "Yesterday", "Earlier"].map((group) =>
              grouped[group].length > 0 ? (
                <div key={group}>
                  <div
                    style={{
                      fontSize: "10.5px",
                      color: "#b8b8b8",
                      padding: "6px 16px 6px",
                      letterSpacing: "0.3px",
                      fontWeight: "500",
                    }}
                  >
                    {group}
                  </div>
                  {grouped[group].map((s) => (
                    <div
                      key={s.session_id}
                      onClick={() => loadSession(s.session_id)}
                      style={{
                        margin: "0 8px 2px",
                        padding: "8px 10px",
                        borderRadius: "8px",
                        cursor: "pointer",
                        background:
                          sessionId === s.session_id ? "#fff" : "transparent",
                        boxShadow:
                          sessionId === s.session_id
                            ? "0 0 0 0.5px #e5e5e5"
                            : "none",
                        display: "flex",
                        alignItems: "center",
                        gap: "9px",
                      }}
                    >
                      <div
                        style={{
                          width: "5px",
                          height: "5px",
                          borderRadius: "50%",
                          background:
                            sessionId === s.session_id ? "#6C63FF" : "#d6d6d6",
                          flexShrink: 0,
                        }}
                      />
                      <span
                        style={{
                          fontSize: "12.5px",
                          color:
                            sessionId === s.session_id ? "#171717" : "#6e6e6e",
                          fontWeight:
                            sessionId === s.session_id ? "500" : "400",
                          flex: 1,
                          minWidth: 0,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {s.session_title}
                      </span>
                      <button
                        onClick={(e) => handleDeleteSession(e, s.session_id)}
                        style={{
                          background: "none",
                          border: "none",
                          color: "#d6d6d6",
                          cursor: "pointer",
                          fontSize: "13px",
                          lineHeight: 1,
                          flexShrink: 0,
                        }}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              ) : null,
            )}
            {sessions.length === 0 && (
              <div
                style={{ padding: "16px", fontSize: "12px", color: "#c4c4c4" }}
              >
                No previous chats
              </div>
            )}
          </div>

          {/* User footer */}
          <div
            style={{
              padding: "12px 14px",
              borderTop: "0.5px solid #ececec",
              display: "flex",
              alignItems: "center",
              gap: "9px",
            }}
          >
            <div
              style={{
                width: "26px",
                height: "26px",
                borderRadius: "50%",
                background: "#eeeeee",
                color: "#6e6e6e",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "10px",
                fontWeight: "600",
                flexShrink: 0,
              }}
            >
              {userInitials}
            </div>
            <span
              style={{
                fontSize: "12.5px",
                color: "#444",
                fontWeight: "500",
                flex: 1,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {parsedUser?.user_name || "User"}
            </span>
          </div>
        </div>

        {/* Main */}
        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            minWidth: 0,
          }}
        >
          {/* Top bar */}
          <div
            style={{
              height: "56px",
              display: "flex",
              alignItems: "center",
              padding: "0 24px",
              gap: "14px",
              borderBottom: "0.5px solid #f0f0f0",
              flexShrink: 0,
            }}
          >
            <span
              style={{
                fontSize: "14px",
                fontWeight: "500",
                color: "#171717",
                flex: 1,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {currentTitle || "New chat"}
            </span>
            <div
              style={{
                display: "flex",
                background: "#f4f4f4",
                borderRadius: "100px",
                padding: "3px",
              }}
            >
              {["my_company", "full_network"].map((s) => (
                <button
                  key={s}
                  onClick={() => setScope(s)}
                  style={{
                    fontSize: "12px",
                    padding: "5px 14px",
                    borderRadius: "100px",
                    border: "none",
                    cursor: "pointer",
                    fontWeight: "500",
                    background: scope === s ? "#171717" : "transparent",
                    color: scope === s ? "#fff" : "#999",
                    transition: "all 0.15s",
                  }}
                >
                  {s === "my_company" ? "My company" : "Full network"}
                </button>
              ))}
            </div>
          </div>

          {/* Thread */}
          <div
            style={{
              flex: 1,
              overflowY: "auto",
              padding: "32px 40px",
              display: "flex",
              flexDirection: "column",
              gap: "24px",
            }}
          >
            {isLoadingHistory && (
              <div
                style={{
                  textAlign: "center",
                  color: "#c4c4c4",
                  fontSize: "13px",
                  marginTop: "40px",
                }}
              >
                Loading conversation...
              </div>
            )}

            {!isLoadingHistory && messages.length === 0 && (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                  textAlign: "center",
                  padding: "40px",
                }}
              >
                <div
                  style={{
                    width: "50px",
                    height: "50px",
                    borderRadius: "14px",
                    background: "#171717",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "18px",
                    fontWeight: "600",
                    color: "#fff",
                    marginBottom: "18px",
                  }}
                >
                  IQ
                </div>
                <div
                  style={{
                    fontSize: "18px",
                    fontWeight: "500",
                    color: "#171717",
                    marginBottom: "8px",
                  }}
                >
                  ConnectIQ
                </div>
                <div
                  style={{
                    fontSize: "13px",
                    color: "#999",
                    lineHeight: "1.6",
                    maxWidth: "380px",
                    marginBottom: "26px",
                  }}
                >
                  Your AI talent assistant. Describe who you're looking for and
                  I'll find the best candidates from your{" "}
                  {scope === "my_company" ? "company" : "full network"}.
                </div>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "8px",
                    width: "100%",
                    maxWidth: "440px",
                  }}
                >
                  {EXAMPLES.map((ex, i) => (
                    <button
                      key={i}
                      onClick={() => handleSend(ex)}
                      style={{
                        padding: "11px 16px",
                        background: "#fafafa",
                        border: "0.5px solid #ececec",
                        borderRadius: "12px",
                        color: "#555",
                        fontSize: "13px",
                        cursor: "pointer",
                        textAlign: "left",
                      }}
                    >
                      {ex}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {!isLoadingHistory &&
              messages.map((msg, i) => (
                <div
                  key={i}
                  style={{
                    display: "flex",
                    gap: "12px",
                    alignItems: "flex-start",
                    flexDirection: msg.role === "user" ? "row-reverse" : "row",
                  }}
                >
                  <div
                    style={{
                      width: "30px",
                      height: "30px",
                      borderRadius: "50%",
                      flexShrink: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "10.5px",
                      fontWeight: "600",
                      background: msg.role === "user" ? "#f0f0f0" : "#171717",
                      color: msg.role === "user" ? "#6e6e6e" : "#fff",
                    }}
                  >
                    {msg.role === "user" ? userInitials : "IQ"}
                  </div>
                  {msg.type === "structured" ? (
                    <div style={{ maxWidth: "86%" }}>
                      <StructuredResult data={msg.data} />
                    </div>
                  ) : (
                    <div
                      style={{
                        maxWidth: "72%",
                        padding: "12px 16px",
                        borderRadius:
                          msg.role === "user"
                            ? "16px 4px 16px 16px"
                            : "4px 16px 16px 16px",
                        background: msg.role === "user" ? "#171717" : "#f7f7f7",
                        color: msg.role === "user" ? "#fff" : "#2b2b2b",
                        fontSize: "13.5px",
                        lineHeight: "1.6",
                      }}
                    >
                      {msg.role === "user"
                        ? msg.content
                        : renderTextContent(msg.content)}
                    </div>
                  )}
                </div>
              ))}

            {isLoading && (
              <div
                style={{
                  display: "flex",
                  gap: "12px",
                  alignItems: "flex-start",
                }}
              >
                <div
                  style={{
                    width: "30px",
                    height: "30px",
                    borderRadius: "50%",
                    background: "#171717",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "10.5px",
                    fontWeight: "600",
                    color: "#fff",
                    flexShrink: 0,
                  }}
                >
                  IQ
                </div>
                <div
                  style={{
                    padding: "13px 16px",
                    background: "#f7f7f7",
                    borderRadius: "4px 16px 16px 16px",
                    display: "flex",
                    gap: "4px",
                    alignItems: "center",
                  }}
                >
                  {[0, 1, 2].map((i) => (
                    <div
                      key={i}
                      style={{
                        width: "6px",
                        height: "6px",
                        borderRadius: "50%",
                        background: "#c4c4c4",
                        animation: `ciq-bounce 1.2s ease-in-out ${i * 0.2}s infinite`,
                      }}
                    />
                  ))}
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Composer */}
          <div
            style={{
              padding: "16px 24px 22px",
              borderTop: "0.5px solid #f0f0f0",
              flexShrink: 0,
            }}
          >
            {scope === "full_network" && (
              <div
                style={{ fontSize: "11px", color: "#999", marginBottom: "8px" }}
              >
                ⚠ Full network — candidate names will be hidden in results
              </div>
            )}
            <div
              style={{
                background: "#f7f7f7",
                borderRadius: "16px",
                display: "flex",
                alignItems: "flex-end",
                gap: "10px",
                padding: "12px 12px 12px 18px",
              }}
            >
              <textarea
                ref={textareaRef}
                rows={1}
                placeholder="Describe what you're looking for..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={isLoading}
                style={{
                  flex: 1,
                  background: "transparent",
                  border: "none",
                  outline: "none",
                  color: "#171717",
                  fontSize: "13.5px",
                  resize: "none",
                  lineHeight: "1.5",
                  maxHeight: "120px",
                  fontFamily: "inherit",
                }}
              />
              <button
                onClick={() => handleSend()}
                disabled={!message.trim() || isLoading}
                style={{
                  width: "32px",
                  height: "32px",
                  borderRadius: "10px",
                  background:
                    !message.trim() || isLoading ? "#e8e8e8" : "#171717",
                  border: "none",
                  color: !message.trim() || isLoading ? "#b8b8b8" : "#fff",
                  cursor:
                    !message.trim() || isLoading ? "not-allowed" : "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  fontSize: "15px",
                }}
              >
                ↑
              </button>
            </div>
            <div
              style={{
                textAlign: "center",
                fontSize: "10.5px",
                color: "#cfcfcf",
                marginTop: "9px",
              }}
            >
              Powered by Claude · ConnectIQ by FirmConnector
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes ciq-bounce {
          0%, 60%, 100% { transform: translateY(0); opacity: 0.4; }
          30% { transform: translateY(-5px); opacity: 1; }
        }
      `}</style>
    </>
  );
};

export default ConnectIQScreen;
